/**
 * One call that opens a direct channel, for either side.
 *
 * The pieces exist separately and are separately tested: signalling.js holds
 * the rules, peer-channel.js turns a finished handshake into something to
 * send over. This is the wiring, kept out of the screens so that both screens
 * do the same thing and neither has a private version of it.
 */

import { watchAttempt } from "./attempt-report";
import { pathOf } from "./connection-path";
import { fetchIceServers } from "./ice";
import { connectPeer } from "./peer-channel";
import { CONNECT_URL, newRoom, roomUrl, socketUrl } from "./signalling";

/** What the desktop puts on screen and the phone scans. */
export function openRoom({ cryptoObject = window.crypto, base = CONNECT_URL } = {}) {
  const { room, secret } = newRoom(cryptoObject);
  return { room, secret, url: roomUrl(base, room, secret) };
}

/**
 * Opens the socket, runs the handshake, and resolves with the channel.
 *
 * Everything that touches the outside is injectable, so the whole path can be
 * driven in a test without a service, a browser or a network.
 */
export async function connect({
  role,
  room,
  secret,
  base = CONNECT_URL,
  SocketImpl = typeof WebSocket !== "undefined" ? WebSocket : null,
  PeerImpl = typeof RTCPeerConnection !== "undefined" ? RTCPeerConnection : null,
  iceServers = null,
  timeoutMs,
}) {
  if (!SocketImpl || !PeerImpl) {
    throw new Error("This browser cannot open a direct connection.");
  }

  // Asked for rather than compiled in: the relay password expires every ten
  // minutes, so there is nothing permanent to build into the app. A service
  // that cannot be reached gives back what the app used to have.
  const servers = iceServers || (await fetchIceServers({ base }));

  const socket = new SocketImpl(socketUrl(role, room, secret, base));
  const peerConnection = new PeerImpl({ iceServers: servers });
  // Watches without taking part, so a failure can say where it stopped.
  const attempt = watchAttempt(peerConnection);

  // A socket that never opens has to fail rather than hang: on a phone that
  // is the difference between a screen that says something and one that
  // spins forever.
  await new Promise((resolve, reject) => {
    socket.onopen = resolve;
    socket.onerror = () => reject(new Error("Could not reach the connection service."));
    socket.onclose = () => reject(new Error("The connection service closed the door."));
  });

  const abandon = () => {
    try { socket.close(); } catch { /* already gone */ }
    try { peerConnection.close(); } catch { /* already gone */ }
  };

  try {
    const channel = await connectPeer({ role, room, socket, peerConnection, timeoutMs }).catch(
      (failure) => {
        // The screen shows whatever is on the error. Without this the phone
        // says "no direct channel" and nothing else, and the three ways this
        // can fail need three different fixes.
        failure.attempt = attempt.summary();
        // A failed attempt that keeps its sockets open leaves the phone
        // holding a connection to the relay for as long as the page is
        // there. One was still open forty seconds after giving up, on
        // 26 July at 21:21.
        abandon();
        throw failure;
      }
    );
    return {
      channel,
      // Which way it actually went. Asked of the connection itself, so the
      // figure is measured rather than assumed.
      path: () => pathOf(peerConnection),
      close: abandon,
    };
  } finally {
    // The room has done its job either way. Leaving it open would keep a
    // rendezvous alive that nobody is using.
    socket.onclose = null;
  }
}
